/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Skeleton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled, alpha, keyframes } from '@mui/material/styles';
import ChatMessage from './ChatMessage';
import ChatInput, { ChatInputHandle } from './ChatInput';
import ChatToolbar from './ChatToolbar';
import { ChatInterfaceProps, ChatFile } from './types';
import { BotMessageSquare } from 'lucide-react';
import HistoryIcon from '@mui/icons-material/History';

const Container = styled(Box)({
  display: 'flex',
  gap: 16,
  height: 'calc(100vh - 150px)',
  margin: 'auto',
});

const HistoryList = styled(Paper)(({ theme }) => ({
  width: 240,
  height: '100%',
  borderRadius: 12,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const HistoryItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(2),
  cursor: 'pointer',
  textAlign: 'left',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.08)
    : 'transparent',
  position: 'relative',
  transition: theme.transitions.create(
    ['background-color', 'border-left-width'],
    {
      duration: theme.transitions.duration.shorter,
    },
  ),
  borderLeft: `${selected ? 3 : 0}px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.12)
      : theme.palette.action.hover,
  },
  '& .MuiTypography-root': {
    transition: theme.transitions.create('color'),
  },
  '& .title': {
    color: selected ? theme.palette.primary.main : theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  '& .timestamp': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

const ChatContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 12,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const Header = styled(Box)(({ theme }) => ({
  height: 64,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  justifyContent: 'space-between',
  boxShadow: theme.shadows[2],
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  display: 'flex',
  flexDirection: 'column',
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[200],
  borderRadius: 12,
  width: 'fit-content',
  marginBottom: theme.spacing(2),
}));

const pulseAnimation = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const AnimatedStopIcon = styled(StopIcon)(({ theme }) => ({
  animation: `${pulseAnimation} 1s ease-in-out infinite`,
  color: theme.palette.error.main,
}));

const ErrorMessage = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  padding: theme.spacing(1, 2),
  borderRadius: 4,
  marginBottom: theme.spacing(2),
  width: 'fit-content',
}));

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  onFileDownload,
  messages,
  onSendMessage,
  onNewChat,
  isTyping,
  isLoading,
  error,
  title,
  onStop,
  history,
  onSelectHistory,
  historyLoading,
  messagesLoading,
  onDeleteHistory,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();
  const chatInputRef = useRef<ChatInputHandle>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isAtBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 100; // pixels from bottom
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    );
  };

  const scrollToBottom = () => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    setShouldAutoScroll(isAtBottom());
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, shouldAutoScroll]);

  useEffect(() => {
    if (!isLoading) {
      chatInputRef.current?.focus();
    }
  }, [isLoading]);

  // always scroll on user message
  useEffect(() => {
    if (messages[messages.length - 1]?.sender === 'user') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  return (
    <Container>
      <HistoryList>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight="medium">
            <HistoryIcon color="primary" sx={{ mr: 1 }} />
            {t('chat.history')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {historyLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <Box
                key={index}
                sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            ))
          ) : history?.length ? (
            history.map((item) => (
              <HistoryItem
                key={item.id}
                selected={item.selected}
                onClick={() => onSelectHistory?.(item.id)}
                onMouseEnter={(e) => {
                  const menuButton =
                    e.currentTarget.querySelector('.menu-button');
                  if (menuButton) {
                    (menuButton as HTMLElement).style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  const menuButton =
                    e.currentTarget.querySelector('.menu-button');
                  if (menuButton) {
                    (menuButton as HTMLElement).style.opacity = '0';
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    className="title"
                    variant="body2"
                    noWrap
                    sx={{ flex: 1, mr: 1 }}
                  >
                    {item.title}
                  </Typography>
                  <IconButton
                    className="menu-button"
                    size="small"
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedItemId(item.id);
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box className="timestamp">
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </HistoryItem>
            ))
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('chat.noHistory')}
              </Typography>
            </Box>
          )}
        </Box>
      </HistoryList>
      <ChatContainer>
        <Header>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BotMessageSquare />
            <Typography
              sx={{
                marginLeft: 1,
              }}
              variant="h6"
            >
              {title ?? 'Chat'}
            </Typography>
          </Box>
          <Tooltip title={t('chat.newChat')}>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => onNewChat && onNewChat()}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Header>
        <MessagesContainer ref={messagesContainerRef}>
          {hasMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                variant="text"
                onClick={() => {
                  setShouldAutoScroll(false);
                  onLoadMore();
                }}
                disabled={messagesLoading}
                startIcon={messagesLoading && <CircularProgress size={16} />}
              >
                {t('chat.messages.loadMore')}
              </Button>
            </Box>
          ) : messages.length > 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: 'block', mb: 2 }}
            >
              {t('chat.messages.noMoreMessages')}
            </Typography>
          ) : null}
          {messagesLoading ? (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 3 }}>
                  <Box sx={{ mr: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      variant="text"
                      width={`${60 - index * 10}%`}
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={60 - index * 10}
                      sx={{ borderRadius: 1 }}
                    />
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            error && (
              <ErrorMessage>
                <Typography variant="body2">{error}</Typography>
              </ErrorMessage>
            )
          )}
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              onRetry={
                message.status === 'error'
                  ? () => onSendMessage(message.content)
                  : undefined
              }
              onFileDownload={onFileDownload}
            />
          ))}
          {isTyping && (
            <TypingIndicator>
              <CircularProgress size={16} />
              <Typography variant="body2" color="textSecondary">
                {t('chat.typing')}
              </Typography>
            </TypingIndicator>
          )}
          {isLoading && !isTyping && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="text"
                color="error"
                size="small"
                startIcon={<AnimatedStopIcon />}
                onClick={onStop}
              >
                {t('chat.stopGenerating')}
              </Button>
            </Box>
          )}
          <div ref={messagesEndRef} />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setDeleteDialogOpen(true);
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              {t('chat.delete')}
            </MenuItem>
          </Menu>
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>{t('chat.deleteHistory')}</DialogTitle>
            <DialogContent>
              <Typography>{t('chat.deleteHistoryConfirm')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                {t('chat.cancel')}
              </Button>
              <Button
                color="error"
                onClick={() => {
                  if (selectedItemId && onDeleteHistory) {
                    onDeleteHistory(selectedItemId);
                  }
                  setDeleteDialogOpen(false);
                  setSelectedItemId(null);
                }}
              >
                {t('chat.delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </MessagesContainer>
        <Box>
          <ChatToolbar disabled={isTyping || isLoading} />
          <ChatInput
            ref={chatInputRef}
            onSendMessage={onSendMessage}
            disabled={isTyping || isLoading || messagesLoading}
          />
        </Box>
      </ChatContainer>
    </Container>
  );
};

export default ChatInterface;
